import type { CellViewWire } from '@badger-sentinel/protocol';

/**
 * Where a cyber card's artwork comes from — vendored edit 1's other half.
 *
 * `<cyber-map>` draws each card's icon as an SVG `<image>` whose href it builds
 * itself as `icons/<pack>/<class>.<ext>`. Edit 1 changed those two tokens from
 * the prototype's `drawio`/`png` to `console`/`svg`, and this is the function
 * that answers the resulting url — so a caller that needs to know where the art
 * lives asks here rather than rebuilding the string.
 *
 * It is deliberately **relative**, exactly as the renderer's own is. The console
 * is a single page with no route segments, so the document is always at the
 * deploy base under every `VITE_BASE_PATH` and `icons/console/router.svg`
 * resolves under it — the one asset path in this client that needs no
 * `import.meta.env.BASE_URL`, because the vendored file could not read one
 * anyway.
 *
 * The eight names are `cyber-map.js`'s `CLASSES`, which is also the set
 * `deviceOf()` can return, and `public/icons/console/README.md` is the argument
 * for drawing them ourselves rather than shipping somebody else's clip art.
 */
export const CYBER_DEVICE_CLASSES: readonly string[] = Object.freeze([
  'router',
  'switch',
  'console',
  'server',
  'sensor',
  'satlink',
  'effector',
  'endpoint',
]);

export function cyberIconHref(cls: string): string {
  return 'icons/console/' + cls + '.svg';
}

/**
 * The device class `<cyber-map>`'s `deviceOf()` will pick for a unit.
 *
 * A **reading** of the vendored function, not a second implementation, and it
 * exists for one reason: the strip and the tests need to say which artwork a
 * card carries without booting a renderer, and a divergence between the two
 * would put one glyph on the canvas and another in the key. The assertion in
 * `test-dom/cyber-board.test.tsx` holds this against what the element actually
 * drew, so the copy cannot rot quietly.
 */
export function deviceClassOf(unit: { readonly icon: string; readonly regime?: string }): string {
  if (unit.icon === 'router') return 'router';
  if (unit.icon === 'cyberoff' || unit.icon === 'cyberdef') return 'console';
  if (unit.icon === 'radar' || unit.icon === 'ssa' || unit.icon === 'msnwarn') return 'sensor';
  if (
    unit.regime !== undefined ||
    unit.icon === 'comms' ||
    unit.icon === 'nav' ||
    unit.icon === 'isr' ||
    unit.icon === 'rpo' ||
    unit.icon === 'asat_co'
  ) {
    return 'satlink';
  }
  if (unit.icon === 'c2' || unit.icon === 'logistics') return 'server';
  if (unit.icon === 'ew' || unit.icon === 'daasat') return 'effector';
  return 'endpoint';
}

/** Every cyber alias in a view — the set the board may draw and no more. */
export function cyberAliases(view: CellViewWire): ReadonlySet<string> {
  return new Set(view.cyber.contacts.map((contact) => contact.alias));
}
